import type { Loan } from '@/types'
import { totalAgeDays } from '@/utils/dealAge'
import { loanCapitalProgram, loanLtvLtc } from '@/pages/pipeline-intelligence/dealResolution'

export type LoanSortKey =
  | 'id'
  | 'property'
  | 'market'
  | 'location'
  | 'product'
  | 'type'
  | 'loanAmount'
  | 'ltv'
  /** LTV-else-LTC, the one leverage figure the Pipeline prints. */
  | 'ltvLtc'
  | 'dscr'
  | 'debtYield'
  | 'stage'
  | 'assignee'
  | 'daysOpen'
  | 'priority'
  | 'noi'
  | 'propertyValue'
  | 'capRate'
  | 'risk'

const PRIORITY_RANK: Record<Loan['priority'], number> = {
  High: 3,
  Medium: 2,
  Low: 1,
}

const TEXT_SORT_KEYS = new Set<LoanSortKey>([
  'id',
  'property',
  'market',
  'location',
  'product',
  'type',
  'assignee',
])

export function marketFromLoan(loan: Loan) {
  return loan.location.split(',')[1]?.trim() ?? ''
}

export function riskTierRank(loan: Loan): number {
  const ltv = loan.ltv <= 1 ? loan.ltv : loan.ltv / 100
  if (ltv > 0.78 || loan.dscr < 1.15) return 2
  if (ltv > 0.70 || loan.dscr < 1.30) return 1
  return 0
}

export function capRateFromLoan(loan: Loan): number {
  if (!loan.propertyValue || !loan.noi) return 0
  return loan.noi / loan.propertyValue
}

function finiteOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/** A value the deal does not state sorts after every stated one, either direction. */
function nullsLast<T>(a: T | null, b: T | null, compare: (x: T, y: T) => number): number {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  return compare(a, b)
}

export function defaultSortDirection(key: LoanSortKey): 'asc' | 'desc' {
  return TEXT_SORT_KEYS.has(key) ? 'asc' : 'desc'
}

export function compareLoans(a: Loan, b: Loan, key: LoanSortKey, dir: 'asc' | 'desc'): number {
  const mul = dir === 'asc' ? 1 : -1
  switch (key) {
    case 'id':
      return mul * a.id.localeCompare(b.id)
    case 'property':
      return mul * a.property.localeCompare(b.property)
    case 'market':
      return mul * marketFromLoan(a).localeCompare(marketFromLoan(b))
    case 'location':
      return mul * a.location.localeCompare(b.location)
    case 'product':
      // The capital program is the product. A deal that states none sorts
      // last, never as an invented "Senior Mortgage".
      return nullsLast(loanCapitalProgram(a), loanCapitalProgram(b), (x, y) => mul * x.localeCompare(y))
    case 'type':
      return mul * a.type.localeCompare(b.type)
    case 'loanAmount':
      return mul * (a.loanAmount - b.loanAmount)
    case 'ltv':
      return nullsLast(finiteOrNull(a.ltv), finiteOrNull(b.ltv), (x, y) => mul * (x - y))
    case 'ltvLtc':
      return nullsLast(loanLtvLtc(a).value, loanLtvLtc(b).value, (x, y) => mul * (x - y))
    case 'dscr':
      return mul * (a.dscr - b.dscr)
    case 'debtYield':
      return mul * ((a.debtYield ?? 0) - (b.debtYield ?? 0))
    case 'stage':
      return mul * (a.stageIndex - b.stageIndex)
    case 'assignee':
      return mul * a.assignee.localeCompare(b.assignee)
    case 'daysOpen':
      // The computed cumulative age; a deal whose age cannot be told sorts as the youngest, not as 0 days
      // alongside the deals that really arrived today.
      return mul * ((totalAgeDays(a) ?? -1) - (totalAgeDays(b) ?? -1))
    case 'priority':
      return mul * (PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority])
    case 'noi':
      return mul * ((a.noi ?? 0) - (b.noi ?? 0))
    case 'propertyValue':
      return mul * ((a.propertyValue ?? 0) - (b.propertyValue ?? 0))
    case 'capRate':
      return mul * (capRateFromLoan(a) - capRateFromLoan(b))
    case 'risk':
      return mul * (riskTierRank(a) - riskTierRank(b))
    default: {
      const _exhaustive: never = key
      return _exhaustive
    }
  }
}

export function sortLoans(loans: Loan[], key: LoanSortKey, dir: 'asc' | 'desc'): Loan[] {
  return [...loans].sort((left, right) => compareLoans(left, right, key, dir))
}
